/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Skeleton,
} from "@chakra-ui/react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import { css } from "@emotion/react";
import { MdClose } from "react-icons/md";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .text-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .subtitle {
    font-size: 18px;
    font-weight: 600;
    line-height: 23px;

    margin-bottom: 12px;
  }

  .text {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
`;

const Flex = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;

  margin-top: 24px;

  img {
    margin-right: 16px;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;

  padding: 8px;

  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    color: #868482;
  }
`;

interface VideoUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function VideoUsersModal({
  isOpen,
  onClose,
}: VideoUsersModalProps) {
  return (
    <Modal motionPreset="scale" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay zIndex="999999999999" />
      <div style={{ zIndex: 999999999999, position: "fixed" }}>
        <ModalContent
          css={css`
            z-index: 999999999999 !important;
          `}
          maxWidth="840px"
          width="calc(100% - 16px)"
          pt="24px"
          pb="48px"
          position="relative"
        >
          <ModalHeader
            fontSize="20px"
            fontWeight="600"
            display="flex"
            alignItems="center"
            justifyContent="start"
            gridGap="8px"
            px={["24px", "40px", "56px", "72px"]}
          >
            <AiOutlineQuestionCircle
              width="20px"
              height="20px"
              color="#FB972E"
            />{" "}
            What’s the difference between Internal & External users?
          </ModalHeader>

          <CloseButtonWrapper onClick={onClose}>
            <MdClose />
          </CloseButtonWrapper>

          <ModalBody px={["24px", "40px", "56px", "72px"]}>
            <Content>
              <Flex>
                <div className="text-wrapper">
                  <div className="text">
                    Unlike most email and CRM platforms, BerserkerMail has two
                    different types of users, internal and external. We do this
                    to protect your application and content by restricting
                    access from those that do not need it. We also save you
                    money by allowing you to purchase more affordable and
                    limited external users when that is all you need.
                  </div>
                </div>
              </Flex>

              <Flex>
                <img src="/video-users-modal/internal.svg" alt="Internal" />
                <div className="text-wrapper">
                  <div className="subtitle">Internal Users</div>
                  <div className="text">
                    Internal users will log into your BerserkerMail account and
                    have access to your entire system, except billing
                    information. In this current version of BerserkerMail all
                    internal users are Admin users. In the future, we will ad
                    more limits and restricts. But for now, any internal user
                    will have full access to your account except billing data.
                  </div>
                </div>
              </Flex>

              <Flex>
                <img src="/video-users-modal/external.svg" alt="External" />

                <div className="text-wrapper">
                  <div className="subtitle">External Users</div>
                  <div className="text">
                    External users do not have a login into your BerserkerMail
                    account. Instead they have a six-digit numeric access code
                    that can only be used with private forms. A private form is
                    used by outsourcers to log actions or work they’ve
                    completed. Private forms can be used to set tags and trigger
                    automations inside of BerserkerMail so they are perfect for
                    outsourcers and team members that need to interact with your
                    system but not have direct access to it.
                  </div>
                </div>
              </Flex>
            </Content>
          </ModalBody>
        </ModalContent>
      </div>
    </Modal>
  );
}
